import type { ComponentProps, ReactNode } from "react";

import { cn } from "../../lib/utils";
import { CLASSES_DO_PAPEL } from "./button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

/**
 * TUT-563a (Lumen 2.0) — o botão SÓ DE ÍCONE (decisão 8 do dono, 29/10).
 *
 * - `label` é obrigatório no tipo: vira o `aria-label` (o nome acessível) e o
 *   conteúdo da dica (`Tooltip` da casa). Botão de ícone sem nome é o defeito
 *   que a P9 (axe) pega tarde; aqui ele não compila, e o `label` vazio que
 *   passa por cast lança em desenvolvimento.
 * - Os papéis são os do `Button` (as MESMAS classes, `CLASSES_DO_PAPEL`):
 *   `discreto` (o padrão), `vidro`, `contorno` e `perigo`. O desfoque do vidro
 *   e a exceção do `data-sem-desfoque` vêm junto, de um lugar só.
 * - Os tamanhos são `sm` (32 px visíveis) e `md` (40). **O `sm` tem 40 px de
 *   área de toque**, com o `::after` em `inset: -4px`, como na prévia
 *   (`docs/design/prototipo-botoes-lumen.html`).
 *
 * Substitui o `iconButtonClass` (`components/ui/chrome.tsx`, web) e os
 * `size="icon*"` do `Button`; os usos de hoje são as pendências da trava
 * `icon-button.test.ts` (web), que a 563b zera.
 */

/** O Vite escreve `import.meta.env.DEV`; o ui-kit não carrega os tipos dele. */
function emDesenvolvimento(): boolean {
  return (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV !== false;
}

export type PapelDoIconButton = "discreto" | "vidro" | "contorno" | "perigo";
export type TamanhoDoIconButton = "sm" | "md";

const CLASSES_DO_TAMANHO: Readonly<Record<TamanhoDoIconButton, string>> = {
  sm: "size-[var(--control-h-compact)] after:absolute after:inset-[-4px] after:content-[''] [&_svg:not([class*='size-'])]:size-[0.9375rem]",
  md: "size-[var(--control-h)]",
};

const base =
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-botao)] border p-0 outline-none transition-[background,color,border-color,box-shadow,transform] duration-[var(--dur-fast)] ease-[var(--ease-out)] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

export type IconButtonProps = Omit<ComponentProps<"button">, "children" | "aria-label"> & {
  /** O nome acessível e a dica ("Mais ações de Marina Souza", "Remover Ana"). */
  label: string;
  /** O glifo (lucide). Fica escondido do leitor de tela: quem fala é o `label`. */
  icone: ReactNode;
  papel?: PapelDoIconButton;
  tamanho?: TamanhoDoIconButton;
  /** O texto da dica, quando difere do nome ("Remover" para "Remover Ana"). */
  dica?: string;
};

/**
 * TUT-563b — a dica abre no FOCO só quando o foco veio do Tab. O codemod
 * levou o "Fechar" de todo diálogo para o `IconButton`, e o diálogo foca o
 * primeiro controle ao abrir: o Radix abria a dica nesse foco programático, e
 * a dica aberta engolia o primeiro Esc (a camada dela fecha antes do diálogo).
 * O diálogo aberto pelo Enter também foca por programa, e por isso a régua é
 * a última tecla ter sido o Tab, e não "foi o teclado". Com o mouse a dica
 * continua no hover; com o Tab, no foco — fora de diálogo. DENTRO de um
 * diálogo (`role="dialog"` ou `<dialog>`) o foco nunca abre a dica: quem chega
 * ao "Fechar" pelo Tab e aperta Esc espera fechar o diálogo, e não a dica
 * (a e2e `dashboard-fullscreen.spec.ts` provou: com a dica aberta, o Esc só
 * fechava ela).
 */
let focoPeloTab = false;
let ouvindoInteracao = false;
function ouvirInteracao(): void {
  if (ouvindoInteracao || typeof document === "undefined") return;
  ouvindoInteracao = true;
  document.addEventListener(
    "keydown",
    (evento) => {
      focoPeloTab = evento.key === "Tab";
    },
    true,
  );
  document.addEventListener(
    "pointerdown",
    () => {
      focoPeloTab = false;
    },
    true,
  );
}

export function IconButton({
  label,
  icone,
  papel = "discreto",
  tamanho = "md",
  dica,
  className,
  type = "button",
  onFocus,
  ...props
}: IconButtonProps) {
  ouvirInteracao();
  if (!label?.trim()) {
    // O tipo já exige; isto pega o `label=""` e o cast. Em produção o botão
    // não cai: avisa no console.
    const mensagem = "IconButton sem label: o label é o nome acessível e a dica do botão.";
    if (emDesenvolvimento()) throw new Error(mensagem);
    console.error(mensagem);
  }
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A identidade vem DEPOIS do resto: como gatilho de outro
              primitivo (`DropdownMenuTrigger asChild`), o `data-slot` dele
              não pode apagar o do botão (P1 agrupa por ele). */}
          <button
            type={type}
            {...props}
            onFocus={(evento) => {
              onFocus?.(evento);
              // O `preventDefault` faz o gatilho do Radix pular a abertura.
              const emDialogo = evento.currentTarget.closest('[role="dialog"],[role="alertdialog"],dialog') !== null;
              if (!focoPeloTab || emDialogo) evento.preventDefault();
            }}
            data-slot="icon-button"
            data-variant={papel}
            data-size={tamanho}
            aria-label={label}
            className={cn(base, CLASSES_DO_PAPEL[papel], CLASSES_DO_TAMANHO[tamanho], className)}
          >
            <span aria-hidden="true" className="contents">
              {icone}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{dica ?? label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
